import { Box, Divider, Flex, Text, VStack } from '@chakra-ui/react';
import isMobileDevice from 'ismobilejs';
import type { GetServerSideProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { isoStringToYmd } from '../../../common/libs/date';
import { sendAnalyticsEvent } from '../../../common/libs/googleAnalytics';
import VideoArticlesGrid from '../../../components/VideoArticlesGrid';
import VideoPlayerContent from '../../../components/VideoPlayerContent';
import { getGaCid } from '../../../server/libs/googleAnalytics';
import {
  getVideo,
  getVideoArticles,
  Video,
  VideoArticle,
} from '../../../server/libs/videoArticles';
import withAuth from '../../../server/libs/withAuth';
import { AntaaAccountType } from '../../../common/libs/antaaAccountType';
import { hasDemoAuthority } from '../../../common/libs/userRoleType';
import { getSpecialArticleProps } from '../../../server/libs/specialArticles';
import createUserAccessHistory from '../../../server/libs/createUserAccessHistory';
import {
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isLicenseVerified,
} from '../../../common/libs/user';
import useSpecialPage from '../../../hooks/useSpecialPage';
import { setting } from './index';

const videoArticlesOrder: 'desc' | 'asc' = 'asc';

const {
  specialPageSlug,
  specialPageShortName,
  tagTexts,
  specialPagePath,
  sideBanner,
} = setting;

const userAccessHistoryOperation = 'special_contents';

type PageContext = {
  isMobile: boolean;
  isDemoAccount: boolean;
  isAuthenticated: boolean;
  isLicenseVerified: boolean;
  isCorporationAccount: boolean;
  isPublisherAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  video: Video;
  videoArticles: VideoArticle[];
  isPrivate: boolean;
};

const SpecialVideoPage: NextPage<PageContext> = ({
  isAuthenticated,
  isLicenseVerified,
  isMobile,
  isDemoAccount,
  isCorporationAccount,
  isPublisherAccount,
  isDoctorRecruiterAccount,
  video,
  videoArticles,
  isPrivate,
}) => {
  const router = useRouter();

  const { isBlocked, blockedContent, pageHead, breadcrumbs } = useSpecialPage({
    specialPageTitle: video.name,
    gaLabelKey: specialPageShortName,
    specialPagePath,
    redirectPath: `${specialPagePath}/${video.videoId}`,
    isAuthenticated,
    isLicenseVerified,
    isCorporationAccount,
    isPublisherAccount,
    isDoctorRecruiterAccount,
    isDemoAccount,
    mainImagePath: video.poster,
    isPrivate,
    needLogin: false,
    needLicenseVerified: false,
    specialPageSlug,
  });

  const onVideoArticleClick = ({ videoId }: { videoId: string }) => {
    router
      .push(
        {
          pathname: router.pathname,
          query: { videoId },
        },
        undefined,
        { scroll: false },
      )
      .then(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      });
  };

  if (isBlocked) {
    return blockedContent;
  }

  return (
    <>
      {pageHead}
      <Box mx={4} pt={[14, 14, 14, 16]} bg="white">
        {breadcrumbs}
        <Flex
          rowGap={4}
          flexDirection={{
            base: 'column',
            md: 'row',
          }}
          flexWrap="wrap"
          justifyContent="space-between"
        >
          <Box
            width={{
              base: '100%',
              md: '65.5%',
            }}
            order={1}
          >
            <VideoPlayerContent
              {...video}
              updateDate={isoStringToYmd(video.updateDate)}
              videoUrl={video.videoUrl}
              poster={video.poster}
              descriptionLabel="概要"
              onPlay={() =>
                sendAnalyticsEvent(
                  'special_contents',
                  'play_video',
                  `${specialPageShortName}_${video.videoId}`,
                )
              }
            />
          </Box>
          <Box
            width={{
              base: '100%',
              md: '32.5%',
            }}
            order={{ base: 3, md: 2 }}
          >
            <VStack spacing={4}>
              {sideBanner({ isDemoAccount, isCorporationAccount })}
            </VStack>
          </Box>
          <Box order={{ base: 2, md: 3 }} width={'100%'} pt={4} pb={6}>
            <Divider borderColor="gray.300" />
            {videoArticles.length > 0 && (
              <Box pt={4}>
                <Flex justifyContent="space-between" mb={4}>
                  <Text fontWeight="bold" fontSize="lg">
                    「{tagTexts[0]}」動画の一覧
                  </Text>
                </Flex>
                <Flex direction={{ base: 'column', md: 'row' }}>
                  <VideoArticlesGrid
                    videoArticles={videoArticles}
                    isMobile={isMobile}
                    gaEventLabel={`${specialPageShortName}_video`}
                    articleId={specialPageSlug}
                    onVideoArticleClick={onVideoArticleClick}
                  />
                </Flex>
              </Box>
            )}
          </Box>
        </Flex>
      </Box>
    </>
  );
};

export default SpecialVideoPage;

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const userId = context.req.user?.id;
    const userAgent = context.req?.headers['user-agent'] ?? '';
    const videoId = String(context.params?.videoId);

    createUserAccessHistory({
      userId,
      userAgent,
      gaCid: getGaCid(context.req),
      operation: userAccessHistoryOperation,
    });

    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;
    const isAuthenticated = context.req.isAuthenticated();
    const isDemoAccount = hasDemoAuthority(context.req.user?.userRoles);

    const { isPrivate, canDisplay } = await getSpecialArticleProps(
      specialPageSlug,
      userId,
    );
    if (!canDisplay) {
      return {
        notFound: true,
      };
    }

    const video = await getVideo(specialPageSlug, videoId);

    if (!video) {
      return {
        notFound: true,
      };
    }

    const videoArticles = await getVideoArticles(
      specialPageSlug,
      videoArticlesOrder,
    );

    return {
      props: {
        isMobile,
        isDemoAccount,
        isAuthenticated,
        isLicenseVerified: isLicenseVerified(context.req.user ?? null),
        isCorporationAccount:
          context.req.user?.antaaAccountType === AntaaAccountType.CORPORATION,
        isPublisherAccount: isPublisherAccount(context.req.user),
        isDoctorRecruiterAccount: isDoctorRecruiterAccount(context.req.user),
        video,
        videoArticles,
        isPrivate,
      },
    };
  },
);
